import React, { createContext, useState, useContext } from 'react';
import { ProductType } from '../Products/Product';

// Define the shape of our cart items
export type CartItem = {
    product: ProductType;
    quantity: number;
    selectedSize?: string;
};

// Define the shape of our cart context
type CartContextType = {
    cart: CartItem[];
    addToCart: (product: ProductType, selectedSize?: string) => void;
    removeFromCart: (productId: number, selectedSize?: string) => void;
    updateQuantity: (productId: number, quantity: number, selectedSize?: string) => void;
    clearCart: () => void;
    getTotalPrice: () => number;
};

// Create the context with a default value
const CartContext = createContext<CartContextType | undefined>(undefined);

// Create a provider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, setCart] = useState<CartItem[]>([]);

    const addToCart = (product: ProductType, selectedSize?: string) => {
        setCart(currentCart => {
            // For armb�nd products, size is required
            if (product.category === "armband" && !selectedSize) {
                console.error("Size is required for armb�nd products");
                return currentCart;
            }

            const existingItem = currentCart.find(item => 
                item.product.id === product.id && 
                (product.category !== "armband" || item.selectedSize === selectedSize)
            );
            
            if (existingItem) {
                return currentCart.map(item =>
                    (item.product.id === product.id && 
                     (product.category !== "armband" || item.selectedSize === selectedSize))
                    ? { ...item, quantity: item.quantity + 1 } 
                    : item
                );
            } else {
                return [...currentCart, { product, quantity: 1, selectedSize }];
            }
        });
    };

    const removeFromCart = (productId: number, selectedSize?: string) => {
        setCart(currentCart => currentCart.filter(item => 
            !(item.product.id === productId && 
              (item.product.category !== "armband" || item.selectedSize === selectedSize))
        ));
    };

    const updateQuantity = (productId: number, quantity: number, selectedSize?: string) => {
        if (quantity <= 0) {
            removeFromCart(productId, selectedSize);
            return;
        }

        setCart(currentCart =>
            currentCart.map(item =>
                (item.product.id === productId && 
                 (item.product.category !== "armband" || item.selectedSize === selectedSize))
                ? { ...item, quantity } 
                : item
            )
        );
    };

    const clearCart = () => {
        setCart([]);
    };

    const getTotalPrice = () => {
        return cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
    };

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotalPrice }}>
            {children}
        </CartContext.Provider>
    );
};

// Create a hook to use the cart context
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};